import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import { ACTIVITY_ID } from '../constants/activity';
import bgImage from '../assets/background.jpg';
import SellerInfoModal from '../components/SellerInfoModal';

const SellersPage = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const api = useAxios();
  const apiRef = useRef(api);
  const hasFetchedRef = useRef(false);

  const fetchSellers = async ({ showErrorToast = true } = {}) => {
    try {
      const response = await apiRef.current.get('/getPayedReservation', {
        params: {
          activityID: ACTIVITY_ID,
          _ts: Date.now(),
        },
      });

      const nextSellers = Array.isArray(response.data) ? response.data : [];
      setSellers(nextSellers);
      return nextSellers;
    } catch (error) {
      console.error('Error fetching sellers:', error);

      if (showErrorToast) {
        toast.error('Myyjälistan haku epäonnistui');
      }

      return null;
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    fetchSellers().finally(() => {
      setLoading(false);
    });
  }, []);

  const openSellerInfo = async (seller) => {
    if (seller?.companyID && isFinite(Number(seller.companyID))) {
      try {
        const response = await apiRef.current.get('/getPublicSeller', {
          params: {
            activityID: ACTIVITY_ID,
            companyID: Number(seller.companyID),
            _ts: Date.now(),
          },
        });

        if (response?.data) {
          setSelectedSeller(response.data);
          return;
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Error fetching seller by companyID:', error);
        }
      }
    }

    // Refresh seller list before opening details so edited company data is current.
    const refreshedSellers = await fetchSellers({ showErrorToast: false });

    if (Array.isArray(refreshedSellers)) {
      const refreshedSeller = refreshedSellers.find((aSeller) => aSeller?.companyID === seller?.companyID)
        ?? refreshedSellers.find((aSeller) => `${aSeller?.name ?? ''}` === `${seller?.name ?? ''}`);

      if (refreshedSeller) {
        setSelectedSeller(refreshedSeller);
        return;
      }
    }

    setSelectedSeller(seller);
  };

  const getLowestSpotNumber = (seller) => {
    if (!Array.isArray(seller?.spotNames) || seller.spotNames.length === 0) {
      return Number.POSITIVE_INFINITY;
    }

    return seller.spotNames.reduce((lowestSpot, spotName) => {
      const parsedSpotNumber = Number.parseInt(`${spotName}`.trim(), 10);

      if (Number.isNaN(parsedSpotNumber)) {
        return lowestSpot;
      }

      return Math.min(lowestSpot, parsedSpotNumber);
    }, Number.POSITIVE_INFINITY);
  };

  const sortedSellers = useMemo(() => {
    return [...sellers].sort((firstSeller, secondSeller) => {
      const firstLowestSpot = getLowestSpotNumber(firstSeller);
      const secondLowestSpot = getLowestSpotNumber(secondSeller);

      if (firstLowestSpot !== secondLowestSpot) {
        return firstLowestSpot - secondLowestSpot;
      }

      return `${firstSeller?.name ?? ''}`.localeCompare(`${secondSeller?.name ?? ''}`, 'fi');
    });
  }, [sellers]);

  const filteredSellers = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase('fi');

    if (!normalizedSearch) {
      return sortedSellers;
    }

    return sortedSellers.filter((seller) => {
      const searchableText = `${seller?.name ?? ''} ${seller?.description ?? ''}`.toLocaleLowerCase('fi');
      return searchableText.includes(normalizedSearch);
    });
  }, [searchValue, sortedSellers]);

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center text-lg text-gray-700">Ladataan myyjiä...</div>;
  }

  if (!sortedSellers.length) {
    return <div className="flex min-h-[60vh] items-center justify-center text-lg text-gray-700">Ei myyjiä näytettäväksi.</div>;
  }

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <div className="mb-6 rounded-2xl border-4 border-blue-500 bg-white/95 p-6 shadow-lg">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Myyjälista</h1>
              <p className="mt-2 text-gray-600">Markkinoilla mukana olevat myyjät ja heidän julkiset yhteystietonsa.</p>
            </div>

            <Link
              to="/myyntipaikat-kartta"
              state={{ hideSpotInfo: true, publicSellerMap: true }}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-700"
            >
              Myyjät kartalla
            </Link>
          </div>

          <div className="mt-5 max-w-2xl">
            <label htmlFor="seller-search" className="mb-2 block text-sm font-semibold text-gray-700">
              Hae myyjiä nimellä tai kuvauksella
            </label>
            <input
              id="seller-search"
              type="text"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Kirjoita hakusana..."
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-gray-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <p className="mt-2 text-sm text-gray-600">
              Näytetään {filteredSellers.length} / {sortedSellers.length} paikan vahvistanutta myyjää.
            </p>
          </div>
        </div>

        {filteredSellers.length ? (
          <div className="space-y-3">
            {filteredSellers.map((seller) => (
              <article
                key={seller.companyID}
                className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm transition hover:shadow-md"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)] sm:items-start sm:gap-4">
                    <div className="flex min-w-[160px] flex-col items-start gap-2">
                      {seller.spotNames?.length ? (
                        seller.spotNames.map((spotName) => (
                          <span
                            key={`${seller.companyID}-${spotName}`}
                            className="inline-flex min-w-[120px] items-center justify-center rounded-full bg-blue-100 px-3 py-1 text-sm font-bold text-blue-800"
                          >
                            Paikka {spotName}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex min-w-[120px] items-center justify-center rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600">
                          Ei paikkaa
                        </span>
                      )}
                    </div>

                    <h2 className="text-lg font-bold text-slate-800 sm:pt-1">{seller.name}</h2>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openSellerInfo(seller)}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:border-blue-500 hover:text-blue-700"
                    >
                      Info
                    </button>

                    <Link
                      to="/myyntipaikat-kartta"
                      state={{
                        hideSpotInfo: true,
                        publicSellerMap: true,
                        sellerMapView: true,
                        sellerCompanyID: seller.companyID,
                        sellerName: seller.name,
                        sellerSpotNames: seller.spotNames ?? [],
                      }}
                      className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
                    >
                      Näytä kartalla
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/90 p-8 text-center text-gray-700 shadow-sm">
            Hakuehdolla ei löytynyt myyjiä.
          </div>
        )}

        <SellerInfoModal isOpen={Boolean(selectedSeller)} seller={selectedSeller} onClose={() => setSelectedSeller(null)} />
      </div>
    </div>
  );
};

export default SellersPage;